import React, { useState, useEffect } from "react";
import { Plus, Save } from "lucide-react";
import { indicatorsApi, clientsApi, datamapsApi } from "../../utils/api";
import { FREQUENCIES } from "../../constants";
import SearchBar from "../common/SearchBar";
import FormModal from "../common/FormModal";
import ActionButtons, {
  PrimaryButton,
  SecondaryButton,
} from "../common/ActionButtons";

const IndicatorManagement = () => {
  const [indicators, setIndicators] = useState([]);
  const [clients, setClients] = useState([]);
  const [datamaps, setDatamaps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingIndicator, setEditingIndicator] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [formData, setFormData] = useState({
    datamapId: 0,
    customerId: 0,
    dataIndicator: "",
    source: "",
    frequency: "",
    active: true,
    token: "",
    clientId: "",
    body: "",
    token_name: "",
    description: "",
    category: "",
    subcategory: "",
    private: false,
  });

  useEffect(() => {
    fetchIndicators();
    fetchClients();
    fetchDatamaps();
  }, []);

  const fetchIndicators = async () => {
    setLoading(true);
    try {
      const data = await indicatorsApi.getAll();
      setIndicators(data);
    } catch (error) {
      console.error("Error fetching indicators:", error);
    }
    setLoading(false);
  };

  const fetchClients = async () => {
    try {
      const data = await clientsApi.getAll();
      setClients(data);
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const fetchDatamaps = async () => {
    try {
      const data = await datamapsApi.getAll();
      setDatamaps(data);
    } catch (error) {
      console.error("Error fetching datamaps:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingIndicator) {
        await indicatorsApi.update(editingIndicator.id, formData);
      } else {
        await indicatorsApi.create(formData);
      }
      fetchIndicators();
      resetForm();
    } catch (error) {
      console.error("Error saving indicator:", error);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this indicator?"))
      return;
    setLoading(true);
    try {
      await indicatorsApi.delete(id);
      fetchIndicators();
    } catch (error) {
      console.error("Error deleting indicator:", error);
    }
    setLoading(false);
  };

  const handleEdit = (indicator) => {
    setEditingIndicator(indicator);
    setFormData({
      datamapId: indicator.datamapid || 0,
      customerId: indicator.customerid || 0,
      dataIndicator: indicator.dataindicator || "",
      source: indicator.source || "",
      frequency: indicator.frequency || "",
      active: indicator.active ?? true,
      token: indicator.token || "",
      clientId: indicator.clientid || "",
      body: indicator.body || "",
      token_name: indicator.token_name || "",
      description: indicator.description || "",
      category: indicator.category || "",
      subcategory: indicator.subcategory || "",
      private: indicator.private ?? false,
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      datamapId: 0,
      customerId: 0,
      dataIndicator: "",
      source: "",
      frequency: "",
      active: true,
      token: "",
      clientId: "",
      body: "",
      token_name: "",
      description: "",
      category: "",
      subcategory: "",
      private: false,
    });
    setEditingIndicator(null);
    setShowForm(false);
  };

  const filteredIndicators = indicators.filter(
    (indicator) =>
      indicator.dataindicator
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      indicator.source?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      indicator.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      indicator.description?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          onRefresh={fetchIndicators}
          placeholder="Search indicators..."
        />
        <PrimaryButton onClick={() => setShowForm(!showForm)} icon={Plus}>
          New Indicator
        </PrimaryButton>
      </div>

      <FormModal
        title={editingIndicator ? "Edit Indicator" : "New Indicator"}
        isOpen={showForm}
        onClose={resetForm}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Data Indicator *
              </label>
              <input
                type="text"
                required
                value={formData.dataIndicator}
                onChange={(e) =>
                  setFormData({ ...formData, dataIndicator: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Source *
              </label>
              <input
                type="text"
                required
                value={formData.source}
                onChange={(e) =>
                  setFormData({ ...formData, source: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows={2}
              placeholder="Enter a detailed description of this indicator"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Frequency *
              </label>
              <select
                required
                value={formData.frequency}
                onChange={(e) =>
                  setFormData({ ...formData, frequency: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select frequency</option>
                {FREQUENCIES.map((freq) => (
                  <option key={freq.value} value={freq.value}>
                    {freq.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Category
              </label>
              <input
                type="text"
                value={formData.category}
                onChange={(e) =>
                  setFormData({ ...formData, category: e.target.value })
                }
                placeholder="e.g., Financial, Operational, Customer"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Subcategory
              </label>
              <input
                type="text"
                value={formData.subcategory}
                onChange={(e) =>
                  setFormData({ ...formData, subcategory: e.target.value })
                }
                placeholder="More specific categorization"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Token
              </label>
              <input
                type="text"
                value={formData.token}
                onChange={(e) =>
                  setFormData({ ...formData, token: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Token Name
            </label>
            <input
              type="text"
              value={formData.token_name}
              onChange={(e) =>
                setFormData({ ...formData, token_name: e.target.value })
              }
              placeholder="Identifier for the authentication token"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Body
            </label>
            <textarea
              value={formData.body}
              onChange={(e) =>
                setFormData({ ...formData, body: e.target.value })
              }
              rows={3}
              placeholder="API request body or additional data (JSON format)"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                DataMap
              </label>
              <select
                value={formData.datamapId}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    datamapId: parseInt(e.target.value) || 0,
                  })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="0">Select DataMap (optional)</option>
                {datamaps.map((datamap) => (
                  <option key={datamap.id} value={datamap.id}>
                    {datamap.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Customer ID
              </label>
              <input
                type="number"
                value={formData.customerId}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    customerId: parseInt(e.target.value) || 0,
                  })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Client
              </label>
              <select
                value={formData.clientId}
                onChange={(e) =>
                  setFormData({ ...formData, clientId: e.target.value })
                }
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select client (optional)</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) =>
                  setFormData({ ...formData, active: e.target.checked })
                }
                className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm font-medium text-slate-700">
                Active
              </span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.private}
                onChange={(e) =>
                  setFormData({ ...formData, private: e.target.checked })
                }
                className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
              />
              <span className="ml-2 text-sm font-medium text-slate-700">
                Private
              </span>
            </label>
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <SecondaryButton onClick={resetForm}>Cancel</SecondaryButton>
            <PrimaryButton type="submit" disabled={loading} icon={Save}>
              {loading ? "Saving..." : "Save Indicator"}
            </PrimaryButton>
          </div>
        </form>
      </FormModal>

      <div className="bg-white rounded-lg shadow-md overflow-hidden border border-slate-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Data Indicator
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Source
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Frequency
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {loading ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-4 text-center text-slate-500"
                  >
                    Loading...
                  </td>
                </tr>
              ) : filteredIndicators.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-4 text-center text-slate-500"
                  >
                    No indicators found
                  </td>
                </tr>
              ) : (
                filteredIndicators.map((indicator) => (
                  <tr
                    key={indicator.id}
                    className="hover:bg-slate-50"
                    title={indicator.description || ""}
                  >
                    <td className="px-6 py-4 text-sm font-medium text-slate-900">
                      <div className="flex items-center gap-2">
                        {indicator.dataindicator}
                        {indicator.private && (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-200 text-slate-700"
                            title="Private indicator"
                          >
                            🔒
                          </span>
                        )}
                      </div>
                    </td>
                    <td
                      className="px-6 py-4 text-sm text-slate-600 max-w-xs truncate"
                      title={indicator.source}
                    >
                      {indicator.source}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {indicator.category || "-"}
                      {indicator.subcategory && (
                        <span className="text-xs text-slate-500">
                          {" "}
                          / {indicator.subcategory}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {indicator.frequency}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          indicator.active
                            ? "bg-green-100 text-green-800"
                            : "bg-slate-100 text-slate-800"
                        }`}
                      >
                        {indicator.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      <ActionButtons
                        onEdit={() => handleEdit(indicator)}
                        onDelete={() => handleDelete(indicator.id)}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default IndicatorManagement;
